import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    projectId: Number,
    projectBudget: Number,
    allocationData: Object,
    projectionData: Object,
    existingProjections: Object,
    disciplineRates: Object
  }

  // Helper function to format numbers with commas
  formatCurrency(amount) {
    return Math.round(amount).toLocaleString()
  }

  connect() {
    this.selectedUsers = [] // Track which users have been added
    this.disciplines = ['Research', 'Design', 'Mechanical', 'Hardware', 'Firmware', 'Admin']

    // Create a mutable copy of the allocation data
    // Stimulus values from HTML attributes can be immutable
    this.mutableAllocationData = JSON.parse(JSON.stringify(this.allocationDataValue || {}))

    // Make this controller globally accessible for remove buttons
    window.taskPlanningController = this


    this.initializeRatesDisplay()
    this.initializeCalculations()
    this.initializeChart()
    this.loadExistingProjectionData()
    this.setupEventListeners()
    this.loadBudgetSummaryData()
  }

  initializeRatesDisplay() {
    // Display discipline rates in table headers
    if (this.disciplineRatesValue) {
      Object.entries(this.disciplineRatesValue).forEach(([discipline, rate]) => {
        const rateElement = document.getElementById(`${discipline.toLowerCase()}-rate`)
        if (rateElement) {
          rateElement.textContent = `$${this.formatCurrency(parseFloat(rate))}/hr`
        }
      })
    }
  }

  initializeCalculations() {
    // Initialize with existing allocation data if any
    this.loadExistingAllocations()
  }

  loadExistingAllocations() {
    // Check if there are existing allocations and auto-add those users
    if (this.mutableAllocationData && typeof this.mutableAllocationData === 'object') {
      const existingUsers = new Set()

      Object.values(this.mutableAllocationData).forEach(taskData => {
        if (taskData.allocated_users) {
          Object.keys(taskData.allocated_users).forEach(userId => {
            existingUsers.add(userId)
          })
        }
      })

      // Auto-add users who have existing allocations
      existingUsers.forEach(userId => {
        const userOption = document.querySelector(`#user-select option[value="${userId}"]`)
        if (userOption) {
          const userName = userOption.dataset.name
          const userTeam = userOption.dataset.team
          const userRate = userOption.dataset.rate

          this.selectedUsers.push({
            id: userId,
            name: userName,
            team: userTeam,
            rate: userRate
          })

          this.addUserColumn(userId, userName, userTeam)
        }
      })

      // Load existing values into inputs
      Object.entries(this.mutableAllocationData).forEach(([taskKey, taskData]) => {
        if (taskData.allocated_users) {
          Object.entries(taskData.allocated_users).forEach(([userId, allocation]) => {
            const input = document.querySelector(`input[data-task="${taskKey}"][data-user="${userId}"]`)
            if (input && allocation.hours) {
              input.value = allocation.hours
            }
          })
        }
      })

      // Rebind event listeners
      this.bindAllocationInputs()

      // Recalculate all totals after loading existing data
      this.recalculateAllTotals()
      this.updateBudgetSummary()
    }
  }

  loadExistingProjectionData() {
    // Load existing projection data from the saved allocation plan
    if (this.existingProjectionsValue && Object.keys(this.existingProjectionsValue).length > 0) {
      Object.entries(this.existingProjectionsValue).forEach(([monthValue, disciplines]) => {
        const hoursData = {}
        Object.entries(disciplines).forEach(([discipline, value]) => {
          // Handle both new format {hours: X, amount: Y} and old format (just amount)
          if (typeof value === 'object' && value.hours !== undefined) {
            // New format - use hours directly
            hoursData[discipline] = value.hours
          } else {
            // Old format - convert dollar amounts back to hours using rates
            const rate = this.disciplineRatesValue[discipline] || 150
            hoursData[discipline] = Math.round(value / rate)
          }
        })

        this.addMonthRow(monthValue, hoursData)
      })

      // Update chart after loading all existing data
      this.updateChart()
    } else if (this.projectionDataValue && this.projectionDataValue.labels) {
      const months = this.projectionDataValue.labels
      const datasets = this.projectionDataValue.datasets || []

      // Create a map of discipline data by month
      const monthData = {}
      months.forEach((month, index) => {
        monthData[month] = {}
        datasets.forEach(dataset => {
          if (dataset.data && dataset.data[index]) {
            // Convert dollar amounts back to hours using rates
            const discipline = dataset.label
            const amount = dataset.data[index]
            const rate = this.disciplineRatesValue[discipline] || 150
            monthData[month][discipline] = Math.round(amount / rate)
          }
        })
      })

      // Add rows for each month
      Object.entries(monthData).forEach(([monthLabel, disciplines]) => {
        // Parse the month label properly to avoid timezone issues
        // monthLabel is like "Jan 2024" or "Feb 2024"
        const date = new Date(monthLabel + ' 1') // Add day to ensure proper parsing
        const year = date.getFullYear()
        const month = (date.getMonth() + 1).toString().padStart(2, '0') // Month is 0-indexed
        const monthValue = `${year}-${month}`
        this.addMonthRow(monthValue, disciplines)
      })

      // Update chart after loading all existing data
      this.updateChart()
    }
  }

  setupEventListeners() {
    // Allocation input changes (will be bound dynamically as users are added)
    this.bindAllocationInputs()

    // Save buttons
    document.getElementById('save-allocations-btn')?.addEventListener('click', this.saveAllocations.bind(this))
    document.getElementById('add-month-btn')?.addEventListener('click', this.showMonthSelector.bind(this))

    // Add user functionality
    document.getElementById('add-user-btn')?.addEventListener('click', this.addUser.bind(this))

    // Month selector modal
    document.getElementById('cancel-month-selection')?.addEventListener('click', this.hideMonthSelector.bind(this))
    document.getElementById('confirm-month-selection')?.addEventListener('click', this.confirmMonthSelection.bind(this))
  }

  bindAllocationInputs() {
    // Create bound handler if it doesn't exist
    if (!this.boundHandleAllocationChange) {
      this.boundHandleAllocationChange = this.handleAllocationChange.bind(this)
    }

    document.querySelectorAll('.allocation-input').forEach(input => {
      input.removeEventListener('input', this.boundHandleAllocationChange) // Remove old listeners
      input.addEventListener('input', this.boundHandleAllocationChange)
    })
  }

  handleAllocationChange(event) {
    const input = event.target
    const taskKey = input.dataset.task
    const userId = input.dataset.user
    const rate = parseFloat(input.dataset.rate) || 0
    const hours = parseFloat(input.value) || 0


    // Update the allocation data structure
    if (!this.mutableAllocationData[taskKey]) {
      this.mutableAllocationData[taskKey] = { allocated_users: {} }
    }

    if (!this.mutableAllocationData[taskKey].allocated_users) {
      this.mutableAllocationData[taskKey].allocated_users = {}
    }

    // Update or remove allocation
    if (hours > 0) {

      this.mutableAllocationData[taskKey].allocated_users[userId] = {
        hours: hours,
        rate: rate,
        cost: hours * rate,
        discipline: this.getUserDiscipline(userId)
      }
    } else {
delete this.mutableAllocationData[taskKey].allocated_users[userId]
    }

    // Recalculate totals
    this.recalculateTaskTotals(taskKey)
    this.recalculateUserTotals(userId)
    this.recalculateGrandTotals()
    this.updateBudgetSummary()
  }

  recalculateTaskTotals(taskKey) {
    const taskData = this.mutableAllocationData[taskKey]
    if (!taskData?.allocated_users) return

    let totalHours = 0
    let totalCost = 0

    Object.values(taskData.allocated_users).forEach(allocation => {
      totalHours += parseFloat(allocation.hours) || 0
      totalCost += parseFloat(allocation.cost) || 0
    })

    // Update UI
    const hoursElement = document.querySelector(`.task-total-hours[data-task="${taskKey}"]`)
    const costElement = document.querySelector(`.task-total-cost[data-task="${taskKey}"]`)

    if (hoursElement) hoursElement.textContent = totalHours.toFixed(1)
    if (costElement) costElement.textContent = this.formatCurrency(totalCost)
  }

  recalculateAllTotals() {
    // Recalculate all task totals
    const taskRows = document.querySelectorAll('#table-body tr[data-task-key]')
    taskRows.forEach(row => {
      const taskKey = row.dataset.taskKey
      this.recalculateTaskTotals(taskKey)
    })

    // Recalculate user totals
    this.selectedUsers.forEach(user => {
      this.recalculateUserTotals(user.id)
    })

    // Recalculate grand totals
    this.recalculateGrandTotals()
  }

  recalculateUserTotals(userId) {
    let userTotalHours = 0

    const userInputs = document.querySelectorAll(`input[data-user="${userId}"]`)
    userInputs.forEach(input => {
      userTotalHours += parseFloat(input.value) || 0
    })

    const userTotalElement = document.querySelector(`.user-total-hours[data-user="${userId}"]`)
    if (userTotalElement) {
      userTotalElement.textContent = userTotalHours.toFixed(1)
    }
  }

  recalculateGrandTotals() {
    let grandTotalHours = 0
    let grandTotalCost = 0

    Object.values(this.mutableAllocationData).forEach(taskData => {
      if (taskData.allocated_users) {
        Object.values(taskData.allocated_users).forEach(allocation => {
          grandTotalHours += parseFloat(allocation.hours) || 0
          grandTotalCost += parseFloat(allocation.cost) || 0
        })
      }
    })

    // Update UI elements if they exist
    this.updateElementSafely('grand-total-hours', grandTotalHours.toFixed(1))
    this.updateElementSafely('grand-total-cost', this.formatCurrency(grandTotalCost))
  }

  updateElementSafely(elementId, value) {
    const element = document.getElementById(elementId)
    if (element) {
      element.textContent = value
      return true
    }
    return false
  }

  async saveAllocations() {
    try {
      const currentMonth = new Date().toISOString().substring(0, 7) // YYYY-MM format

      const response = await fetch(`/projects/${this.projectIdValue}/allocation_plans`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          allocation_plan: {
            month: currentMonth,
            allocation_data: this.buildAllocationDataForSave()
          }
        })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.chart_data) {
          this.updateChart(data.chart_data)
        }
        this.showNotification('Allocation plan saved successfully!', 'success')
      } else {
        const errorData = await response.json()
        console.error('Server error:', errorData)
        this.showNotification(
          `Failed to save: ${errorData.details ? errorData.details.join(', ') : errorData.error}`,
          'error'
        )
        throw new Error('Failed to save allocations')
      }
    } catch (error) {
      console.error('Error saving allocations:', error)
      this.showNotification('Failed to save allocations', 'error')
    }
  }


  buildAllocationDataForSave() {
    // Build unified data structure matching AllocationPlan's expected format
    const saveData = {
      tasks: {
        data: {},
        totals: { total_hours: 0, total_amount: 0 }
      },
      monthly_plan: {
        data: {},
        totals: { total_hours: 0, total_amount: 0 }
      },
      grand_totals: { total_hours: 0, total_amount: 0 }
    }

    // Add task allocations
    Object.entries(this.mutableAllocationData).forEach(([taskKey, taskData]) => {
      if (taskData.allocated_users && Object.keys(taskData.allocated_users).length > 0) {
        saveData.tasks.data[taskKey] = taskData.allocated_users
        // Calculate task totals
        Object.values(taskData.allocated_users).forEach(userAlloc => {
          saveData.tasks.totals.total_hours += parseFloat(userAlloc.hours || 0)
          saveData.tasks.totals.total_amount += parseFloat(userAlloc.cost || 0)
        })
      }
    })

    // Add projection data from the Monthly Spend Planning table with both hours AND amounts
    document.querySelectorAll('#projection-table-body tr').forEach(row => {
      const month = row.dataset.month
      if (!month) return

      saveData.monthly_plan.data[month] = {}
      this.disciplines.forEach(discipline => {
        const input = row.querySelector(`input[data-discipline="${discipline}"]`)
        const hours = parseFloat(input?.value || 0)
        if (hours > 0) {
          const rate = this.disciplineRatesValue[discipline] || 150
          const amount = hours * rate
          // Save both hours and amount
          saveData.monthly_plan.data[month][discipline] = {
            hours: hours,
            amount: amount
          }
          // Update monthly totals
          saveData.monthly_plan.totals.total_hours += hours
          saveData.monthly_plan.totals.total_amount += amount
        }
      })
    })

    // Calculate grand totals
    saveData.grand_totals.total_hours = saveData.tasks.totals.total_hours + saveData.monthly_plan.totals.total_hours
    saveData.grand_totals.total_amount = saveData.tasks.totals.total_amount + saveData.monthly_plan.totals.total_amount

    return saveData
  }

  initializeChart() {
    const ctx = document.getElementById('projection-chart')
    if (!ctx) return

    // Initialize with empty data if no projection data exists
    const chartData = this.projectionDataValue || {
      labels: [],
      datasets: []
    }

    // Ensure datasets have proper colors
    if (chartData.datasets) {
      chartData.datasets.forEach(dataset => {
        dataset.backgroundColor = this.getDisciplineColor(dataset.label)
      })
    }

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 0.5,
        scales: {
          x: {
            stacked: true,
            grid: {
              color: 'rgba(156, 163, 175, 0.2)'
            }
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grid: {
              color: 'rgba(156, 163, 175, 0.2)'
            },
            ticks: {
              callback: function(value) {
                return '$' + value.toLocaleString()
              }
            }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: function(context) {
                const hours = context.raw / (context.dataset.rate || 150)
                return `${context.dataset.label}: ${hours.toFixed(0)}h ($${context.parsed.y.toLocaleString()})`
              }
            }
          },
          legend: {
            position: 'top'
          }
        }
      }
    })
  }

  updateChart() {
    if (!this.chart) return

    // Collect data from projection table
    const monthRows = document.querySelectorAll('#projection-table-body tr')
    const months = []
    const disciplineData = {}

    // Initialize discipline data structure
    this.disciplines.forEach(discipline => {
      disciplineData[discipline] = []
    })

    // Collect data from each row
    monthRows.forEach(row => {
      const monthLabel = row.querySelector('td:first-child').textContent
      months.push(monthLabel)

      this.disciplines.forEach(discipline => {
        const input = row.querySelector(`input[data-discipline="${discipline}"]`)
        const hours = parseFloat(input?.value || 0)
        const rate = this.disciplineRatesValue[discipline] || 150
        disciplineData[discipline].push(hours * rate) // Convert to dollars for chart
      })
    })

    // Update chart data
    this.chart.data.labels = months
    this.chart.data.datasets = this.disciplines.map(discipline => ({
      label: discipline,
      data: disciplineData[discipline],
      backgroundColor: this.getDisciplineColor(discipline),
      rate: this.disciplineRatesValue[discipline] // Store rate for tooltip
    }))

    this.chart.update()
  }

  addUser() {
    const userSelect = document.getElementById('user-select')
    const selectedOption = userSelect.options[userSelect.selectedIndex]

    if (!selectedOption || !selectedOption.value) {
      this.showNotification('Please select a user to add', 'error')
      return
    }

    const userId = selectedOption.value
    const userName = selectedOption.dataset.name
    const userTeam = selectedOption.dataset.team
    const userRate = selectedOption.dataset.rate || 0

    // Check if user is already added
    if (this.selectedUsers.some(user => user.id === userId)) {
      this.showNotification('This user is already added to the table', 'error')
      return
    }

    // Add user to tracking array
    this.selectedUsers.push({
      id: userId,
      name: userName,
      team: userTeam,
      rate: userRate
    })

    // Add column to table
    this.addUserColumn(userId, userName, userTeam)

    // Reset dropdown
    userSelect.selectedIndex = 0

    // Rebind event listeners
    this.bindAllocationInputs()

    this.showNotification(`Added ${userName} to allocation table`, 'success')
  }

  addUserColumn(userId, userName, userTeam) {
    // Add header column
    const headerRow = document.querySelector('#table-header tr')
    const totalHoursHeader = headerRow.children[headerRow.children.length - 2] // Before "Total Hours"

    const headerCell = document.createElement('th')
    headerCell.className = 'px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'
    headerCell.style.width = '10%'
    headerCell.innerHTML = `
      <div class="flex items-center justify-center space-x-2">
        <div>
          ${userName}<br>
          <span class="text-xs text-gray-400">(${userTeam})</span>
        </div>
        <button onclick="taskPlanningController.removeUser('${userId}')" class="text-red-500 hover:text-red-700 text-xs" title="Remove user">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `
    headerRow.insertBefore(headerCell, totalHoursHeader)

    // Add input cells for each task row
    const taskRows = document.querySelectorAll('#table-body tr[data-task-key]')
    taskRows.forEach(row => {
      const taskKey = row.dataset.taskKey
      const totalHoursCell = row.children[row.children.length - 2] // Before "Total Hours"

      const inputCell = document.createElement('td')
      inputCell.className = 'px-6 py-4 whitespace-nowrap text-center'
      inputCell.innerHTML = `
        <input type="number"
               step="0.5"
               min="0"
               placeholder="0"
               data-task="${taskKey}"
               data-user="${userId}"
               data-rate="${this.selectedUsers.find(u => u.id === userId)?.rate || 0}"
               class="allocation-input w-16 text-center border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white">
      `
      row.insertBefore(inputCell, totalHoursCell)
    })

    // Add footer total cell (now table-based again for proper alignment)
    const footerRow = document.querySelector('#footer-row')
    // Insert at index 4 (after the 4 fixed columns: Task, Original Estimate, Logged, Remaining)
    const grandTotalHours = footerRow.children[footerRow.children.length - 2] // Before "Grand Total Hours"

    const footerCell = document.createElement('td')
    footerCell.className = 'px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-gray-900 dark:text-white'
    footerCell.style.width = '10%'
    footerCell.innerHTML = `<span class="user-total-hours" data-user="${userId}">0</span>h`
    footerRow.insertBefore(footerCell, grandTotalHours)
  }

  removeUser(userId) {
    // Clean up allocation data for this user first
    Object.keys(this.mutableAllocationData).forEach(taskKey => {
      if (this.mutableAllocationData[taskKey].allocated_users && this.mutableAllocationData[taskKey].allocated_users[userId]) {
        delete this.mutableAllocationData[taskKey].allocated_users[userId]
      }
    })

    // Remove from tracking array
    this.selectedUsers = this.selectedUsers.filter(user => user.id !== userId)

    // Find the column index
    const headerRow = document.querySelector('#table-header tr')
    let columnIndex = -1

    Array.from(headerRow.children).forEach((cell, index) => {
      if (cell.innerHTML.includes(`removeUser('${userId}')`)) {
        columnIndex = index
      }
    })

    if (columnIndex === -1) return

    // Remove header column
    headerRow.children[columnIndex].remove()

    // Remove input cells from task rows
    const taskRows = document.querySelectorAll('#table-body tr[data-task-key]')
    taskRows.forEach(row => {
      if (row.children[columnIndex]) {
        row.children[columnIndex].remove()
      }
    })

    // Remove user total from footer (now table-based again)
    const userTotalInFooter = document.querySelector(`#footer-row [data-user="${userId}"]`)
    if (userTotalInFooter && userTotalInFooter.parentElement) {
      userTotalInFooter.parentElement.remove()
    }

    // Recalculate totals after cleanup
    this.recalculateAllTotals()
    this.updateBudgetSummary()

    this.showNotification(`Removed user from allocation table`, 'success')
  }

  showMonthSelector() {
    document.getElementById('month-selection-modal').classList.remove('hidden')
  }

  hideMonthSelector() {
    document.getElementById('month-selection-modal').classList.add('hidden')
  }

  confirmMonthSelection() {
    const monthSelector = document.getElementById('month-selector')
    const selectedMonth = monthSelector.value

    // Check if month already exists
    const tbody = document.getElementById('projection-table-body')
    const existingMonths = Array.from(tbody.querySelectorAll('tr')).map(row => {
      return row.dataset.month
    }).filter(Boolean)

    if (existingMonths.includes(selectedMonth)) {
      this.showNotification('This month already exists in the projection table', 'error')
      return
    }

    // Add row to projection table
    this.addMonthRow(selectedMonth)

    // Update chart after adding month
    this.updateChart()

    // Hide modal
    this.hideMonthSelector()

    this.showNotification(`Added month to budget projection`, 'success')
  }

  addMonthRow(month, existingData = {}) {
    const tbody = document.getElementById('projection-table-body')
    const row = document.createElement('tr')
    row.dataset.month = month

    // Format month for display - parse year and month separately to avoid timezone issues
    const [year, monthNum] = month.split('-')
    const monthDate = new Date(parseInt(year), parseInt(monthNum) - 1, 1) // Month is 0-indexed in JS
    const monthDisplay = monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })

    // Build discipline input cells
    const disciplineCells = this.disciplines.map(discipline => {
      const hours = existingData[discipline] || 0
      return `
        <td class="px-4 py-3 whitespace-nowrap text-center">
          <input type="number"
                 class="projection-input w-16 text-center border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm dark:bg-gray-700 dark:text-white"
                 data-month="${month}"
                 data-discipline="${discipline}"
                 placeholder="0"
                 min="0"
                 step="0.5"
                 value="${hours || ''}">
        </td>
      `
    }).join('')

    row.innerHTML = `
      <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
        ${monthDisplay}
      </td>
      ${disciplineCells}
      <td class="px-4 py-3 whitespace-nowrap text-center text-sm font-medium text-gray-900 dark:text-white">
        <span class="month-total-hours" data-month="${month}">0</span>h /<br>
        $<span class="month-total-amount" data-month="${month}">0</span>
      </td>
      <td class="px-4 py-3 whitespace-nowrap text-center">
        <button class="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                onclick="taskPlanningController.removeMonthRow('${month}')">
          <i class="fas fa-trash"></i>
        </button>
      </td>
    `

    tbody.appendChild(row)

    // Add event listeners to new inputs
    row.querySelectorAll('.projection-input').forEach(input => {
      input.addEventListener('input', this.updateProjectionTotals.bind(this))
    })

    // Calculate initial totals if there's existing data
    if (Object.keys(existingData).length > 0) {
      this.updateProjectionTotals()
    }
  }

  removeMonthRow(month) {
    const row = document.querySelector(`tr[data-month="${month}"]`)
    if (row) {
      row.remove()
      this.updateProjectionTotals()
      this.updateChart()
      this.showNotification('Month removed from projection', 'success')
    }
  }

  updateProjectionTotals() {
    // Update monthly totals
    const monthRows = document.querySelectorAll('#projection-table-body tr')
    let grandTotalHours = 0
    let grandTotalAmount = 0

    // Track discipline totals
    const disciplineTotals = {}
    this.disciplines.forEach(d => disciplineTotals[d] = 0)

    monthRows.forEach(row => {
      const month = row.dataset.month
      let monthHours = 0
      let monthAmount = 0

      this.disciplines.forEach(discipline => {
        const input = row.querySelector(`input[data-discipline="${discipline}"]`)
        const hours = parseFloat(input?.value || 0)
        const rate = this.disciplineRatesValue[discipline] || 150

        monthHours += hours
        monthAmount += hours * rate
        disciplineTotals[discipline] += hours
      })

      // Update month totals
      const hoursElement = row.querySelector('.month-total-hours')
      const amountElement = row.querySelector('.month-total-amount')
      if (hoursElement) hoursElement.textContent = monthHours.toFixed(1)
      if (amountElement) amountElement.textContent = this.formatCurrency(monthAmount)

      grandTotalHours += monthHours
      grandTotalAmount += monthAmount
    })

    // Update discipline column totals
    this.disciplines.forEach(discipline => {
      const totalElement = document.getElementById(`${discipline.toLowerCase()}-total-hours`)
      if (totalElement) {
        totalElement.textContent = disciplineTotals[discipline].toFixed(1)
      }
    })

    // Update grand totals
    const grandHoursElement = document.getElementById('projection-grand-total-hours')
    const grandAmountElement = document.getElementById('projection-grand-total-amount')
    if (grandHoursElement) grandHoursElement.textContent = grandTotalHours.toFixed(1)
    if (grandAmountElement) grandAmountElement.textContent = this.formatCurrency(grandTotalAmount)

    // Update chart
    this.updateChart()

    // Update budget summary
    this.updateBudgetSummary()
  }



  updateChart(chartData) {
    if (!this.chart) return

    // If no chartData provided, generate it from current table data
    if (!chartData) {
      chartData = this.generateChartDataFromTable()
    }

    if (chartData) {
      this.chart.data = chartData
      this.chart.update()
    }
  }

  generateChartDataFromTable() {
    const monthRows = document.querySelectorAll('#projection-table-body tr')
    if (monthRows.length === 0) {
      return { labels: [], datasets: [] }
    }

    // Collect all months and discipline data
    const monthData = {}
    const allDisciplines = new Set()

    monthRows.forEach(row => {
      const month = row.dataset.month
      if (!month) return

      monthData[month] = {}
      this.disciplines.forEach(discipline => {
        const input = row.querySelector(`input[data-discipline="${discipline}"]`)
        const hours = parseFloat(input?.value || 0)
        if (hours > 0) {
          const rate = this.disciplineRatesValue[discipline] || 150
          monthData[month][discipline] = hours * rate
          allDisciplines.add(discipline)
        }
      })
    })

    // Sort months chronologically
    const sortedMonths = Object.keys(monthData).sort()

    // Format month labels
    const labels = sortedMonths.map(month => {
      const [year, monthNum] = month.split('-')
      const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1)
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    })

    // Create datasets for each discipline
    const datasets = Array.from(allDisciplines).sort().map(discipline => {
      const data = sortedMonths.map(month => monthData[month][discipline] || 0)
      return {
        label: discipline,
        data: data,
        backgroundColor: this.getDisciplineColor(discipline)
      }
    })

    return { labels: labels, datasets: datasets }
  }

  getDisciplineColor(discipline) {
    const colors = {
      'Strategy': 'rgb(245, 158, 11)',    // Warm amber
      'Design': 'rgb(239, 68, 68)',       // Vibrant red
      'Mechanical': 'rgb(251, 146, 60)',  // Rich orange
      'Hardware': 'rgb(34, 197, 94)',     // Fresh green
      'Firmware': 'rgb(59, 130, 246)',    // Clear blue
      'Admin': 'rgb(107, 114, 128)',      // Professional grey
      'Research': 'rgb(245, 158, 11)'     // Using Strategy color for Research
    }
    return colors[discipline] || 'rgb(17, 24, 39)' // Deep charcoal as default
  }

  updateBudgetSummary() {
    // Calculate Task Planning totals
    let taskPlanningHours = 0
    let taskPlanningAmount = 0

    Object.values(this.mutableAllocationData).forEach(taskData => {
      if (taskData.allocated_users) {
        Object.values(taskData.allocated_users).forEach(allocation => {
          taskPlanningHours += parseFloat(allocation.hours) || 0
          taskPlanningAmount += parseFloat(allocation.cost) || 0
        })
      }
    })


    // Calculate Monthly Planning totals
    let monthlyPlanningHours = 0
    let monthlyPlanningAmount = 0

    document.querySelectorAll('#projection-table-body tr').forEach(row => {
      this.disciplines.forEach(discipline => {
        const input = row.querySelector(`input[data-discipline="${discipline}"]`)
        const hours = parseFloat(input?.value || 0)
        const rate = this.disciplineRatesValue[discipline] || 150

        monthlyPlanningHours += hours
        monthlyPlanningAmount += hours * rate
      })
    })

    // Update Task Planning display
    const taskHoursEl = document.getElementById('task-planning-hours')
    const taskAmountEl = document.getElementById('task-planning-amount')

    if (taskHoursEl) taskHoursEl.textContent = taskPlanningHours.toFixed(1)
    if (taskAmountEl) taskAmountEl.textContent = this.formatCurrency(taskPlanningAmount)

    // Update Monthly Planning display
    const monthlyHoursEl = document.getElementById('monthly-planning-hours')
    const monthlyAmountEl = document.getElementById('monthly-planning-amount')
    if (monthlyHoursEl) monthlyHoursEl.textContent = monthlyPlanningHours.toFixed(1)
    if (monthlyAmountEl) monthlyAmountEl.textContent = this.formatCurrency(monthlyPlanningAmount)

    // Calculate remaining budget
    const invoicedAmount = parseFloat(document.getElementById('invoiced-amount')?.textContent.replace(/,/g, '') || 0)
    const loggedAmount = parseFloat(document.getElementById('logged-amount')?.textContent.replace(/,/g, '') || 0)
    const projectBudget = this.projectBudgetValue || 0
    const remaining = projectBudget - (loggedAmount + invoicedAmount + taskPlanningAmount + monthlyPlanningAmount)

    const remainingEl = document.getElementById('remaining-budget')
    if (remainingEl) {
      remainingEl.textContent = this.formatCurrency(remaining)
      // Update color based on remaining amount
      remainingEl.parentElement.className = remaining < 0 ?
        'text-lg font-semibold text-red-600 dark:text-red-400' :
        'text-lg font-semibold text-amber-600 dark:text-amber-400'
    }
  }

  async loadBudgetSummaryData() {
    try {
// Fetch logged time and invoiced amounts from the server
      const response = await fetch(`/projects/${this.projectIdValue}/budget_summary`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      })

if (response.ok) {
        const data = await response.json()
// Update logged time
        if (data.logged_hours !== undefined) {
document.getElementById('logged-hours').textContent = parseFloat(data.logged_hours).toFixed(1)
          document.getElementById('logged-amount').textContent = this.formatCurrency(parseFloat(data.logged_amount))
        }

        // Update invoiced amount
        if (data.invoiced_amount !== undefined) {
document.getElementById('invoiced-amount').textContent = this.formatCurrency(parseFloat(data.invoiced_amount))
        }

        // Recalculate remaining budget
        this.updateBudgetSummary()
      } else {
        // Budget summary response failed silently
      }
    } catch (error) {
      console.error('Error loading budget summary data:', error)
    }
  }

  getUserDiscipline(userId) {
    // This would need to be passed from the server or fetched
    // For now, return a default
    return 'Admin'
  }

  showNotification(message, type = 'info') {
    // Create a simple notification
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg ${
      type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
      type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
      'bg-blue-100 text-blue-800 border border-blue-200'
    }`
    notification.textContent = message

    document.body.appendChild(notification)

    // Remove after 3 seconds
    setTimeout(() => {
      notification.remove()
    }, 3000)
  }
}